/**
 * TableElementEditor Component
 * Complete table editor for Table content items with:
 * - Visual table grid with editable cells
 * - Add/remove rows and columns
 * - Header row and footer row toggles
 * - Cell types: Text, Number, Formula
 * - Column width adjustment
 * - CSV import/export
 * - Preview mode
 */

import { useState, useCallback, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Plus,
  Eye,
  Edit3,
  Download,
  Upload,
  Columns,
  Rows,
  ChevronDown,
  Type,
  Table as TableIcon,
  Settings2,
} from 'lucide-react'
import type {
  TableElementContent,
  TableHeader,
  TableRow,
  TableCell,
  TableStyle,
} from '@/lib/api/types'
import { TableGrid } from './table/TableGrid'
import { CSVImportDialog } from './table/CSVImportDialog'
import { CSVExportDialog } from './table/CSVExportDialog'
import { TablePreview } from './table/TablePreview'
import { TableStyleEditor } from './table/TableStyleEditor'

// ============================================
// Types
// ============================================

export interface TableElementEditorProps {
  value: Partial<TableElementContent>
  onChange: (content: Partial<TableElementContent>) => void
  disabled?: boolean
  className?: string
}

// ============================================
// Helper Functions
// ============================================

/**
 * Generate a unique ID for rows
 */
function generateRowId(): string {
  return `row_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Generate a unique key for columns
 */
function generateColumnKey(): string {
  return `col_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Create default headers with initial columns
 */
function createDefaultHeaders(count: number = 3): TableHeader[] {
  return Array.from({ length: count }, (_, i) => ({
    key: generateColumnKey(),
    label: `Column ${i + 1}`,
    dataType: 'Text' as const,
    width: 150,
    alignment: 'Left' as const,
  }))
}

/**
 * Create a default row with empty cells for each column
 */
function createDefaultRow(headers: TableHeader[]): TableRow {
  const cells: Record<string, TableCell> = {}
  headers.forEach((header) => {
    cells[header.key] = { value: '', displayValue: '' }
  })
  return {
    id: generateRowId(),
    cells,
    order: 0,
  }
}

/**
 * Create default rows
 */
function createDefaultRows(headers: TableHeader[], count: number = 3): TableRow[] {
  return Array.from({ length: count }, (_, i) => ({
    ...createDefaultRow(headers),
    id: generateRowId(),
    order: i,
  }))
}

// ============================================
// Toolbar Component
// ============================================

interface TableToolbarProps {
  onAddColumn: () => void
  onAddRow: () => void
  onToggleHeader: () => void
  onToggleFooter: () => void
  onImport: () => void
  onExport: () => void
  onTogglePreview: () => void
  onOpenStyleEditor: () => void
  hasHeaderRow: boolean
  hasFooterRow: boolean
  isPreviewMode: boolean
  disabled?: boolean
  columnCount: number
  rowCount: number
}

function TableToolbar({
  onAddColumn,
  onAddRow,
  onToggleHeader,
  onToggleFooter,
  onImport,
  onExport,
  onTogglePreview,
  onOpenStyleEditor,
  hasHeaderRow,
  hasFooterRow,
  isPreviewMode,
  disabled = false,
  columnCount,
  rowCount,
}: TableToolbarProps) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-2 p-2 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 rounded-t-xl">
      <div className="flex items-center gap-1">
        {/* Add Column */}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onAddColumn}
                disabled={disabled || isPreviewMode}
                className="h-8 gap-1.5 px-2"
              >
                <Columns className="h-4 w-4" />
                <Plus className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Add Column
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Add Row */}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onAddRow}
                disabled={disabled || isPreviewMode}
                className="h-8 gap-1.5 px-2"
              >
                <Rows className="h-4 w-4" />
                <Plus className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Add Row
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Header Toggle */}
        <div className="flex items-center gap-2 px-2">
          <Switch
            id="has-header"
            checked={hasHeaderRow}
            onCheckedChange={onToggleHeader}
            disabled={disabled || isPreviewMode}
            className="scale-75"
          />
          <Label htmlFor="has-header" className="text-xs text-neutral-600 dark:text-neutral-400 cursor-pointer">
            Header
          </Label>
        </div>

        {/* Footer Toggle */}
        <div className="flex items-center gap-2 px-2">
          <Switch
            id="has-footer"
            checked={hasFooterRow}
            onCheckedChange={onToggleFooter}
            disabled={disabled || isPreviewMode}
            className="scale-75"
          />
          <Label htmlFor="has-footer" className="text-xs text-neutral-600 dark:text-neutral-400 cursor-pointer">
            Footer
          </Label>
        </div>

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Import/Export Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              className="h-8 gap-1.5 px-2"
            >
              <TableIcon className="h-4 w-4" />
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={onImport} disabled={isPreviewMode}>
              <Upload className="h-4 w-4 mr-2" />
              Import CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExport}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onOpenStyleEditor} disabled={isPreviewMode}>
              <Settings2 className="h-4 w-4 mr-2" />
              Table Style
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-center gap-2">
        {/* Table Stats */}
        <span className="text-xs text-neutral-500 dark:text-neutral-400 mr-2">
          {columnCount} columns, {rowCount} rows
        </span>

        {/* Preview Toggle */}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onTogglePreview}
                disabled={disabled}
                className={cn(
                  'h-8 gap-1.5 px-2.5 rounded-lg',
                  isPreviewMode && 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                )}
              >
                {isPreviewMode ? (
                  <>
                    <Edit3 className="h-4 w-4" />
                    <span className="text-sm">Edit</span>
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4" />
                    <span className="text-sm">Preview</span>
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {isPreviewMode ? 'Switch to edit mode' : 'Preview styled table'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  )
}

// ============================================
// Main TableElementEditor Component
// ============================================

export function TableElementEditor({
  value,
  onChange,
  disabled = false,
  className,
}: TableElementEditorProps) {
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [showExportDialog, setShowExportDialog] = useState(false)
  const [showStyleEditor, setShowStyleEditor] = useState(false)
  const [selectedCell, setSelectedCell] = useState<{ rowId: string; columnKey: string } | null>(null)

  // Initialize default values if not present
  const headers = useMemo(() => {
    if (value.headers && value.headers.length > 0) {
      return value.headers
    }
    return createDefaultHeaders(3)
  }, [value.headers])

  const rows = useMemo(() => {
    if (value.rows && value.rows.length > 0) {
      return value.rows
    }
    return createDefaultRows(headers, 3)
  }, [value.rows, headers])

  const hasHeaderRow = value.hasHeaderRow ?? true
  const hasFooterRow = value.style?.borderStyle !== undefined // Use style presence as proxy for footer

  // Update the content with new values
  const updateContent = useCallback(
    (updates: Partial<TableElementContent>) => {
      onChange({
        ...value,
        ...updates,
        rowCount: updates.rows?.length ?? rows.length,
        columnCount: updates.headers?.length ?? headers.length,
      })
    },
    [value, onChange, rows.length, headers.length]
  )

  // ============================================
  // Column Operations
  // ============================================

  const handleAddColumn = useCallback(() => {
    const newHeader: TableHeader = {
      key: generateColumnKey(),
      label: `Column ${headers.length + 1}`,
      dataType: 'Text',
      width: 150,
      alignment: 'Left',
    }
    const newHeaders = [...headers, newHeader]

    // Add empty cell for new column to all rows
    const newRows = rows.map((row) => ({
      ...row,
      cells: {
        ...row.cells,
        [newHeader.key]: { value: '', displayValue: '' },
      },
    }))

    updateContent({ headers: newHeaders, rows: newRows })
  }, [headers, rows, updateContent])

  const handleUpdateColumn = useCallback(
    (columnKey: string, updates: Partial<TableHeader>) => {
      const newHeaders = headers.map((h) =>
        h.key === columnKey ? { ...h, ...updates } : h
      )
      updateContent({ headers: newHeaders })
    },
    [headers, updateContent]
  )

  const handleDeleteColumn = useCallback(
    (columnKey: string) => {
      if (headers.length <= 1) return // Keep at least one column

      const newHeaders = headers.filter((h) => h.key !== columnKey)
      const newRows = rows.map((row) => {
        const newCells = { ...row.cells }
        delete newCells[columnKey]
        return { ...row, cells: newCells }
      })

      updateContent({ headers: newHeaders, rows: newRows })
    },
    [headers, rows, updateContent]
  )

  const handleMoveColumn = useCallback(
    (columnKey: string, direction: 'left' | 'right') => {
      const currentIndex = headers.findIndex((h) => h.key === columnKey)
      if (currentIndex === -1) return

      const newIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1
      if (newIndex < 0 || newIndex >= headers.length) return

      const newHeaders = [...headers]
      const [removed] = newHeaders.splice(currentIndex, 1)
      if (removed) {
        newHeaders.splice(newIndex, 0, removed)
        updateContent({ headers: newHeaders })
      }
    },
    [headers, updateContent]
  )

  // ============================================
  // Row Operations
  // ============================================

  const handleAddRow = useCallback(() => {
    const newRow = createDefaultRow(headers)
    newRow.order = rows.length
    updateContent({ rows: [...rows, newRow] })
  }, [headers, rows, updateContent])

  const handleUpdateCell = useCallback(
    (rowId: string, columnKey: string, cellValue: Partial<TableCell>) => {
      const newRows = rows.map((row) => {
        if (row.id !== rowId) return row
        const existingCell = row.cells[columnKey] || { value: '', displayValue: '' }
        return {
          ...row,
          cells: {
            ...row.cells,
            [columnKey]: {
              value: cellValue.value ?? existingCell.value,
              displayValue: cellValue.displayValue ?? existingCell.displayValue,
              isFormula: cellValue.isFormula ?? existingCell.isFormula,
              formula: cellValue.formula ?? existingCell.formula,
            } as TableCell,
          },
        }
      })
      updateContent({ rows: newRows })
    },
    [rows, updateContent]
  )

  const handleDeleteRow = useCallback(
    (rowId: string) => {
      if (rows.length <= 1) return // Keep at least one row

      const newRows = rows
        .filter((r) => r.id !== rowId)
        .map((r, i) => ({ ...r, order: i }))
      updateContent({ rows: newRows })
    },
    [rows, updateContent]
  )

  const handleMoveRow = useCallback(
    (rowId: string, direction: 'up' | 'down') => {
      const currentIndex = rows.findIndex((r) => r.id === rowId)
      if (currentIndex === -1) return

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
      if (newIndex < 0 || newIndex >= rows.length) return

      const newRows = [...rows]
      const [removed] = newRows.splice(currentIndex, 1)
      if (removed) {
        newRows.splice(newIndex, 0, removed)
        // Update order
        newRows.forEach((r, i) => (r.order = i))
        updateContent({ rows: newRows })
      }
    },
    [rows, updateContent]
  )

  const handleInsertRow = useCallback(
    (rowId: string, position: 'above' | 'below') => {
      const currentIndex = rows.findIndex((r) => r.id === rowId)
      if (currentIndex === -1) return

      const newRow = createDefaultRow(headers)
      const insertIndex = position === 'above' ? currentIndex : currentIndex + 1

      const newRows = [...rows]
      newRows.splice(insertIndex, 0, newRow)
      // Update order
      newRows.forEach((r, i) => (r.order = i))
      updateContent({ rows: newRows })
    },
    [rows, headers, updateContent]
  )

  const handleDuplicateRow = useCallback(
    (rowId: string) => {
      const row = rows.find((r) => r.id === rowId)
      if (!row) return

      const currentIndex = rows.findIndex((r) => r.id === rowId)
      const newRow: TableRow = {
        id: generateRowId(),
        cells: { ...row.cells },
        order: currentIndex + 1,
      }

      const newRows = [...rows]
      newRows.splice(currentIndex + 1, 0, newRow)
      // Update order
      newRows.forEach((r, i) => (r.order = i))
      updateContent({ rows: newRows })
    },
    [rows, updateContent]
  )

  // ============================================
  // Import/Export Handlers
  // ============================================

  const handleImportData = useCallback(
    (importedHeaders: TableHeader[], importedRows: TableRow[]) => {
      updateContent({
        headers: importedHeaders,
        rows: importedRows,
        hasHeaderRow: true,
      })
      setShowImportDialog(false)
    },
    [updateContent]
  )

  // ============================================
  // Style Handlers
  // ============================================

  const handleStyleChange = useCallback(
    (style: TableStyle) => {
      updateContent({ style })
      setShowStyleEditor(false)
    },
    [updateContent]
  )

  // ============================================
  // Toggle Handlers
  // ============================================

  const handleToggleHeader = useCallback(() => {
    updateContent({ hasHeaderRow: !hasHeaderRow })
  }, [hasHeaderRow, updateContent])

  const handleToggleFooter = useCallback(() => {
    // Toggle footer by modifying style (placeholder for actual footer implementation)
    const currentStyle = value.style || {}
    updateContent({
      style: {
        ...currentStyle,
        // Toggle footer indicator
        cellPadding: currentStyle.cellPadding,
      },
    })
  }, [value.style, updateContent])

  // ============================================
  // Render
  // ============================================

  return (
    <div className={cn('space-y-0', className)}>
      <div className="border border-neutral-200 dark:border-neutral-700 rounded-xl overflow-hidden bg-white dark:bg-neutral-950">
        {/* Toolbar */}
        {!disabled && (
          <TableToolbar
            onAddColumn={handleAddColumn}
            onAddRow={handleAddRow}
            onToggleHeader={handleToggleHeader}
            onToggleFooter={handleToggleFooter}
            onImport={() => setShowImportDialog(true)}
            onExport={() => setShowExportDialog(true)}
            onTogglePreview={() => setIsPreviewMode(!isPreviewMode)}
            onOpenStyleEditor={() => setShowStyleEditor(true)}
            hasHeaderRow={hasHeaderRow}
            hasFooterRow={hasFooterRow}
            isPreviewMode={isPreviewMode}
            disabled={disabled}
            columnCount={headers.length}
            rowCount={rows.length}
          />
        )}

        {/* Editor / Preview Content */}
        <div className="relative">
          {isPreviewMode ? (
            <TablePreview
              headers={headers}
              rows={rows}
              hasHeaderRow={hasHeaderRow}
              style={value.style}
              className="min-h-[200px]"
            />
          ) : (
            <TableGrid
              headers={headers}
              rows={rows}
              hasHeaderRow={hasHeaderRow}
              selectedCell={selectedCell}
              onSelectCell={setSelectedCell}
              onUpdateCell={handleUpdateCell}
              onUpdateColumn={handleUpdateColumn}
              onDeleteColumn={handleDeleteColumn}
              onMoveColumn={handleMoveColumn}
              onDeleteRow={handleDeleteRow}
              onMoveRow={handleMoveRow}
              onInsertRow={handleInsertRow}
              onDuplicateRow={handleDuplicateRow}
              disabled={disabled}
            />
          )}
        </div>

        {/* Footer Stats */}
        <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400 px-3 py-2 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 rounded-b-xl">
          <div className="flex items-center gap-4">
            <span>{headers.length} column{headers.length !== 1 ? 's' : ''}</span>
            <span>{rows.length} row{rows.length !== 1 ? 's' : ''}</span>
          </div>
          {hasHeaderRow && (
            <span className="flex items-center gap-1">
              <Type className="h-3.5 w-3.5" />
              Header row enabled
            </span>
          )}
        </div>
      </div>

      {/* Import Dialog */}
      <CSVImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        onImport={handleImportData}
      />

      {/* Export Dialog */}
      <CSVExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        headers={headers}
        rows={rows}
      />

      {/* Style Editor Dialog */}
      <TableStyleEditor
        open={showStyleEditor}
        onOpenChange={setShowStyleEditor}
        style={value.style}
        onStyleChange={handleStyleChange}
      />
    </div>
  )
}

export default TableElementEditor
